import Time from '@/gf/modules/Time'
import { gql, useQuery } from '@apollo/client'

const doc = gql`
  query ($filters: [[String]]!, $timezone: String!) {
    fetchRfqReportingMetrics(filters: $filters, timezone: $timezone) {
      averageTimeToQuote
      rfqsQuotedCount
      chartData {
        averageTimeToQuote
        rfqsQuotedCount
        date
      }
      allRfqs {
        timeToQuote
        storeOrder {
          shortId
          id
          insertedAt
        }
        requestForQuote {
          shortId
          id
          insertedAt
          owners {
            id
            displayName
          }
        }
      }
      ownersBreakdown {
        averageTimeToQuote
        rfqsQuotedCount
        owner {
          id
          displayName
        }
      }
    }
  }
`

type Data = {
  fetchRfqReportingMetrics: {
    averageTimeToQuote: number
    rfqsQuotedCount: number
    chartData: {
      averageTimeToQuote: number | null
      rfqsQuotedCount: number | null
      date: string
    }[]
    allRfqs: {
      timeToQuote: number
      storeOrder: {
        id: string
        shortId: string
        insertedAt: string
      }
      requestForQuote: {
        id: string
        shortId: string
        insertedAt: string
        owners: {
          id: string
          displayName: string
        }[]
      }
    }[]
    ownersBreakdown: {
      averageTimeToQuote: number
      rfqsQuotedCount: number
      owner: {
        id: string
        displayName: string
      }
    }[]
  }
}

type Vars = {
  filters: string[][]
  timezone: string
}

type Props = Omit<Vars, 'filters'> & {
  filters?: Vars['filters']
}

const useRfqReportingMetrics = ({ filters = [], timezone }: Props) => {
  const { loading, error, data } = useQuery<Data, Vars>(doc, {
    variables: { filters, timezone },
    fetchPolicy: 'no-cache',
  })

  const reportingMetrics = data && {
    ...data.fetchRfqReportingMetrics,
    allRfqs: data.fetchRfqReportingMetrics.allRfqs.map((rfq) => ({
      ...rfq,
      storeOrder: {
        ...rfq.storeOrder,
        insertedAt: Time.fromPayload(rfq.storeOrder.insertedAt),
      },
      requestForQuote: {
        ...rfq.requestForQuote,
        insertedAt: Time.fromPayload(rfq.requestForQuote.insertedAt),
      },
    })),
  }

  return { loading, error, data: reportingMetrics }
}

export type ReportingMetrics = NonNullable<ReturnType<typeof useRfqReportingMetrics>['data']>

useRfqReportingMetrics.Query = doc
export default useRfqReportingMetrics
